import { create } from 'zustand';
import { auth as authApi, projects as projectsApi, projectGroups as groupsApi, tasks as tasksApi, sprints as sprintsApi, boards as boardsApi } from '../api';

const apiCache = new Map(); // Simple in-memory cache

const cachedApiCall = async (key, fn, ttl = 3 * 60 * 1000) => { // 3 min TTL
  const now = Date.now();
  if (apiCache.has(key)) {
    const { data, timestamp } = apiCache.get(key);
    if (now - timestamp < ttl) return data;
  }
  const data = await fn();
  apiCache.set(key, { data, timestamp: now });
  return data;
};

export const useStore = create((set, get) => ({
  // ── Auth ────────────────────────────────────────────
  user: null,
  token: localStorage.getItem('token'),
  theme: localStorage.getItem('theme') || 'light',
  setTheme: (t) => {
    localStorage.setItem('theme', t);
    set({ theme: t });
    document.documentElement.classList.toggle('dark', t === 'dark');
  },
  login: async (data) => {
    const res = await authApi.login(data);
    localStorage.setItem('token', res.token);
    set({ token: res.token, user: res.user });
  },
  register: async (data) => {
    const res = await authApi.register(data);
    localStorage.setItem('token', res.token);
    set({ token: res.token, user: res.user });
  },
  logout: () => {
    localStorage.removeItem('token');
    set({ token: null, user: null, projects: [], activeProject: null });
  },
  fetchMe: async () => {
    try {
      const user = await authApi.me();
      set({ user });
    } catch { get().logout(); }
  },
  updateMe: async (data) => {
    const user = await authApi.updateMe(data);
    set({ user });
    return user;
  },
  // ── Projects ─────────────────────────────────────────
  projects: [],
  activeProject: null,
  fetchProjects: async () => {
    const projects = await cachedApiCall('projects', projectsApi.list);
    set({ projects });
    await get().fetchProjectGroups();
    return projects;
  },
  setActiveProject: (p) => set({ activeProject: p, tasks: [], sprints: [], boards: [] }),
  createProject: async (data) => {
    const p = await projectsApi.create(data);
    set(s => ({ projects: [p, ...s.projects] }));
    return p;
  },
  updateProject: async (id, data) => {
    const p = await projectsApi.update(id, data);
    set(s => ({ projects: s.projects.map(x => x._id === id ? p : x), activeProject: s.activeProject?._id === id ? p : s.activeProject }));
  },
  deleteProject: async (id) => {
    await projectsApi.delete(id);
    set(s => ({ projects: s.projects.filter(x => x._id !== id), activeProject: s.activeProject?._id === id ? null : s.activeProject }));
  },
  // ── Project Groups ────────────────────────────────────
  projectGroups: [],
  fetchProjectGroups: async () => {
    const groups = await groupsApi.list();
    set({ projectGroups: groups });
    return groups;
  },
  createProjectGroup: async (data) => {
    const g = await groupsApi.create(data);
    set(s => ({ projectGroups: [...s.projectGroups, g] }));
    return g;
  },
  updateProjectGroup: async (id, data) => {
    const g = await groupsApi.update(id, data);
    set(s => ({ projectGroups: s.projectGroups.map(x => x._id === id ? g : x) }));
  },
  deleteProjectGroup: async (id) => {
    await groupsApi.delete(id);
    set(s => ({ projectGroups: s.projectGroups.filter(x => x._id !== id) }));
  },
  // ── Tasks ─────────────────────────────────────────────
  tasks: [],
  myTasks: [],
  myTasksLoading: false,
  activeTask: null,
  taskModalOpen: false,
  fetchTasks: async (params) => {
    const tasks = await tasksApi.list(params);
    set({ tasks });
    return tasks;
  },
  fetchMyTasks: async (params = {}) => {
    const userId = params.userId || get().user?._id;
    if (!userId) return [];
    set({ myTasksLoading: true });
    try {
      const myTasks = await tasksApi.list({ ...params, assignee: userId });
      set({ myTasks });
      return myTasks;
    } finally {
      set({ myTasksLoading: false });
    }
  },
  createTask: async (data) => {
    const task = await tasksApi.create(data);
    set(s => ({ tasks: [...s.tasks, task] }));
    return task;
  },
  updateTask: async (id, data) => {
    const prevState = {
      tasks: get().tasks,
      myTasks: get().myTasks,
      activeTask: get().activeTask,
    };

    // Optimistic update: apply immediately in UI
    set(s => ({
      tasks: s.tasks.map(t => t._id === id ? { ...t, ...data } : t),
      myTasks: (s.myTasks || []).map(t => t._id === id ? { ...t, ...data } : t),
      activeTask: s.activeTask?._id === id ? { ...s.activeTask, ...data } : s.activeTask
    }));

    try {
      const task = await tasksApi.update(id, data);
      // Sync with canonical backend state
      set(s => ({
        tasks: s.tasks.map(t => t._id === id ? task : t),
        myTasks: (s.myTasks || []).map(t => t._id === id ? task : t),
        activeTask: s.activeTask?._id === id ? { ...s.activeTask, ...task } : s.activeTask
      }));
      return task;
    } catch (err) {
      // Rollback only on error
      set({
        tasks: prevState.tasks,
        myTasks: prevState.myTasks,
        activeTask: prevState.activeTask,
      });
      throw err;
    }
  },
  deleteTask: async (id) => {
    await tasksApi.delete(id);
    set(s => ({ tasks: s.tasks.filter(t => t._id !== id), taskModalOpen: false, activeTask: null }));
  },
  openTask: async (id) => {
    const task = await tasksApi.get(id);
    set({ activeTask: task, taskModalOpen: true });
  },
  closeTask: () => set({ taskModalOpen: false, activeTask: null }),
  // ── Sprints ─────────────────────────────────────────
  sprints: [],
  fetchSprints: async (projectId) => {
    const sprints = await sprintsApi.list(projectId);
    set({ sprints });
    return sprints;
  },
  createSprint: async (data) => {
    const s = await sprintsApi.create(data);
    set(st => ({ sprints: [...st.sprints, s] }));
    return s;
  },
  updateSprint: async (id, data) => {
    const s = await sprintsApi.update(id, data);
    set(st => ({ sprints: st.sprints.map(x => x._id === id ? s : x) }));
  },
  startSprint: async (id, data) => {
    const s = await sprintsApi.start(id, data);
    set(st => ({ sprints: st.sprints.map(x => x._id === id ? s : (x.status === 'active' ? { ...x, status: 'completed' } : x)) }));
  },
  completeSprint: async (id, data) => {
    const s = await sprintsApi.complete(id, data);
    set(st => ({ sprints: st.sprints.map(x => x._id === id ? s : x) }));
    // Refresh tasks to reflect moved tasks
    if (get().activeProject) get().fetchTasks({ project: get().activeProject._id });
  },
  deleteSprint: async (id) => {
    await sprintsApi.delete(id);
    set(st => ({ sprints: st.sprints.filter(x => x._id !== id) }));
  },
  // ── Boards ─────────────────────────────────────────
  boards: [],
  activeBoard: null,
  fetchBoards: async (projectId) => {
    const boards = await boardsApi.list(projectId);
    set({ boards, activeBoard: boards[0] || null });
    return boards;
  },
  updateBoard: async (id, data) => {
    const b = await boardsApi.update(id, data);
    set(s => ({ boards: s.boards.map(x => x._id === id ? b : x), activeBoard: s.activeBoard?._id === id ? b : s.activeBoard }));
  },
}));
